import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { exhaustMap, filter, map, of, tap } from 'rxjs';
import { Option } from '@sapphire/result';

import { EffectsBase } from '@ps/core-data-access';
import { AuthSelectorsFacade } from '@ps/auth-data-access';
import { ToastService } from '@ps/core-overlay';
import { ProjectService } from '@ps/survey-data-access';
import {
  SURVEY_MESSAGES,
  TRACKER_MESSAGES,
  TRACKER_PRE_DRAFT_MINIMUM_SELECTED,
} from '@ps/survey-utils';

import {
  selectFeasibilityCpi,
  selectInsightsChildren,
  selectPricing,
} from '../insights';
import { selectQualificationsRecord } from '../reference/survey-creation-reference.selectors';

import {
  SurveyCreationTrackerActions,
  SurveyCreationTrackerApiActions,
} from './survey-creation-audience.actions';
import { selectAudienceChildren } from './survey-creation-audience.selectors';
import {
  selectCoreChildren,
  selectTrackerConfig,
} from '../core/survey-creation-core.selectors';
import {
  buildPreDraftChildDrafts,
  buildPreDraftChildDraftsContext,
} from './utils/pre-draft-save.helper';

@Injectable()
export class SurveyCreationTrackerSaveEffects extends EffectsBase {
  private readonly _actions$ = inject(Actions);
  private readonly _store = inject(Store);
  private readonly _projectService = inject(ProjectService);
  private readonly _authSelectors = inject(AuthSelectorsFacade);
  private readonly _toastService = inject(ToastService);

  public readonly createTrackerProject$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationTrackerActions.createTrackerProject),
      concatLatestFrom(() => [
        this._store.select(selectAudienceChildren),
        this._store.select(selectCoreChildren),
        this._store.select(selectPricing),
        this._store.select(selectFeasibilityCpi),
        this._store.select(selectInsightsChildren),
        this._store.select(selectQualificationsRecord),
        this._store.select(selectTrackerConfig),
      ]),
      map(
        ([
          ,
          children,
          coreChildren,
          pricingState,
          feasibilityCpi,
          insightsChildren,
          qualificationsRecord,
          trackerConfig,
        ]) => {
          const context = buildPreDraftChildDraftsContext({
            children,
            coreChildren,
            pricingState,
            feasibilityCpi,
            insightsChildren,
            qualificationsRecord,
            user: this._authSelectors.user(),
            companyId: this._authSelectors.activeCompanyId(),
            noSignedInUserMessage: TRACKER_MESSAGES.NO_SIGNED_IN_USER,
            tooFewSelectedMessage: TRACKER_MESSAGES.SELECT_AT_LEAST_TWO_WAVES,
            minimumSelected: TRACKER_PRE_DRAFT_MINIMUM_SELECTED,
            invalidChildMessage: TRACKER_MESSAGES.WAVE_NOT_SAVEABLE,
          });

          return [
            context.andThen(buildPreDraftChildDrafts),
            Option.from(trackerConfig),
          ] as const;
        },
      ),
      tap(([drafts]) =>
        drafts.inspectErr((message) => this._toastService.error(message)),
      ),
      filter(([drafts]) => drafts.isOk()),
      exhaustMap(([drafts, trackerConfig]) => {
        const unwrappedDrafts = drafts.unwrap();

        return trackerConfig.match({
          some: (config) =>
            this._projectService
              .createTrackerProject({
                children: unwrappedDrafts.map((draft) => draft.input),
                trackerConfig: {
                  ...config,
                  numberOfWaves: unwrappedDrafts.length,
                },
              })
              .pipe(
                map((survey) => {
                  this._toastService.success(SURVEY_MESSAGES.SAVED, {
                    keepOpen: false,
                  });
                  return SurveyCreationTrackerApiActions.createTrackerProjectSuccess(
                    { survey },
                  );
                }),
                this.psCatchError(
                  (error) =>
                    of(
                      SurveyCreationTrackerApiActions.createTrackerProjectFailure(
                        { error: error.message },
                      ),
                    ),
                  { notify: true },
                ),
              ),
          none: () => {
            this._toastService.error(TRACKER_MESSAGES.WAVE_NOT_SAVEABLE);

            return of(
              SurveyCreationTrackerApiActions.createTrackerProjectFailure({
                error: TRACKER_MESSAGES.WAVE_NOT_SAVEABLE,
              }),
            );
          },
        });
      }),
    ),
  );
}
